import React, { useEffect, useMemo } from 'react';
import { IAddLotsDialogProperties } from './AddLotsDialog.properties';
import FormViewModal from 'components/common/Modals/FormViewModal';
import { generateForm } from './AddLotsDialog.form';
import { omit } from 'lodash';
import { useGetLotDetails } from 'api/putawayLots/lots';

const AddLotsDialog = (props: IAddLotsDialogProperties): JSX.Element => {
    const { open, onCancel, isBinTracked, productId, warehouseId, defaultBin = '', calculateLotsHandler, editData, formValues, changeFormField } = props;
    const formSchema = generateForm(isBinTracked);

    const [initialValues, setInitialValues] = React.useState<any>({
        Bin: defaultBin,
    });

    const [selectedLot, setSelectedLot] = React.useState();
    // Use API inline metadata to drive Price editability
    // Default to read-only until the API tells us otherwise (matches prior behavior where Price was disabled by default).
    const [priceReadOnly, setPriceReadOnly] = React.useState<boolean>(true);

    const getLotDetailsMutation = useGetLotDetails();

    const extractLotDetail = (response: any): any => {
        if (!response) {
            return null;
        }

        // Support common response shapes (inline schema / direct object)
        return (
            response?.LotDetail?.inlineObject ??
            response?.LotDetail ??
            response?.inlineObject ??
            response
        );
    };

    useEffect(
        () => {
            if (!open || !productId) {
                return;
            }

            const baseCriteria = editData
                ? omit(editData, ['FastLine', 'LotSerialChanged'])
                : { ...(defaultBin ? { Bin: defaultBin } : {}) };

            // Set something immediately so the form isn't empty while loading.
            setInitialValues(baseCriteria);

            getLotDetailsMutation.mutateAsync({
                ProductId: Number(productId),
                Initialise: !Boolean(editData),
                LotCriteria: {
                    ...(!isBinTracked && { Bin: '' }),
                    ...(baseCriteria ?? {}),
                }
            }).then((response) => {
                const fromApi = extractLotDetail(response);
                console.log('fromApi', fromApi);
                if (fromApi) {
                    // Keep default bin if API returns null/undefined for Bin on initialise.
                    const merged = {
                        ...(baseCriteria ?? {}),
                        ...fromApi,
                        ...(fromApi?.Bin == null && baseCriteria?.Bin != null ? { Bin: baseCriteria.Bin } : {}),
                    };

                    setPriceReadOnly(Boolean(merged?.PriceReadOnly));
                    setInitialValues(merged);
                } else {
                    setPriceReadOnly(Boolean(baseCriteria?.PriceReadOnly));
                }
            });
        },
        [open, editData, productId, defaultBin, isBinTracked]);

    const okHandler = () => {
        const transformed = {
            ...formValues,
            LotSerial: selectedLot,
            ...(!editData && { FastLine: true })
        };
        calculateLotsHandler(transformed, true, editData ? true : false);
    };

    const disabledOk = useMemo(
        () => {
            if (isBinTracked) {
                return !formValues?.LotSerial || !formValues?.Bin;
            } else {
                return !formValues?.LotSerial;
            }
        },
        [isBinTracked, formValues]
    );

    const getActions = () => {
        return [
            {
                title: 'Ok',
                iconName: 'CheckCircle',
                disabled: disabledOk,
                listener: async () => {
                    okHandler();
                }
            },
            {
                title: 'Cancel',
                isDefault: true,
                iconName: 'Cancel',
                listener: () => {
                    if (onCancel) {
                        onCancel();
                    }
                },
            },
        ];
    };

    return (
        <div>
            <FormViewModal
                open={open}
                loading={Boolean(getLotDetailsMutation?.isLoading)}
                title='Lot Number Detail'
                formProps={{
                    style: {
                        width: 500,
                    },
                    formName: 'AddLotsDailog',
                    formSchema,
                    productId: productId,
                    warehouse: warehouseId,
                    bin: '',
                    isWarehouseProductBinScoped: true,
                    initialValues: initialValues,
                    fieldExtensions: ({
                        LotSerial: {
                            onSelectedItemChange: (details, data) => {
                                setSelectedLot(data);

                                // When a Lot is selected, populate Expiry from the lookup item's details (ExpiryDate).
                                const expiryFromLookup = details?.Details?.ExpiryDate ?? data?.Details?.ExpiryDate ?? null;
                                changeFormField?.('Expiry', expiryFromLookup);
                            },
                            disabled: editData
                        },
                        Price: {
                            disabled: priceReadOnly,
                        },
                    }),
                }}
                actions={getActions()}
                dialogActionsButtons={true}
                dialogActionsShadow={false}
            />
        </div>
    );
};
export default AddLotsDialog;
